/**
 * AgentSessionModal — opens a true OS-level pop-out window for agent sessions.
 *
 * Mounted at the QueueView level (sibling of RejectDialog), driven by
 * `useQueueStore.agentSessionModalSessionId`. Renders nothing — the "modal"
 * IS the new OS window, not a DOM dialog.
 *
 * When `sessionId` becomes non-null the component:
 *   1. Derives a stable Tauri window label from the session ID (prefix `sv-`).
 *   2. If a window with that label is already open, focuses it.
 *   3. Otherwise creates a new `WebviewWindow` loading `session-window.html`
 *      with sessionId / agentId / userId / consumerId / env as query params.
 *   4. Immediately calls `closeAgentSessionModal()` so the queue store is
 *      reset, allowing the same session to be re-opened later.
 *
 * The session-window.html entry point bootstraps its own API client from the
 * URL params and renders `SessionWindowPage` — a full-screen, read-only,
 * portrait-oriented transcript viewer with a live watch-stream subscription.
 */

import { useEffect, type ReactNode } from 'react'
import { openPopout } from '@/lib/popout'
import { useQueueStore } from '../../store'
import { useOperatorStore } from '@/shared/stores/operator-store'
import { useEnvironmentStore } from '@/shared/stores/environment-store'
import { toast } from '@/shared/hooks/use-toast'

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Format a snake_case agent id as "Title Case" for the window title bar. */
function formatAgentName(id: string | null | undefined): string {
  if (!id) return 'Agent'
  return id
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/**
 * Derive a stable, label-safe Tauri window identifier from a session ID.
 * Tauri window labels must match `[a-zA-Z0-9_-/]+`, max 64 chars.
 * We prefix with `sv-` so the capability's `"sv-*"` glob matches.
 */
function sessionLabel(sessionId: string): string {
  return `sv-${sessionId.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}`
}

// ── Component ────────────────────────────────────────────────────────────────

export function AgentSessionModal(): ReactNode {
  const sessionId = useQueueStore((s) => s.agentSessionModalSessionId)

  useEffect(() => {
    if (!sessionId) return

    const openSessionWindow = async (): Promise<void> => {
      // ── Read everything from store snapshots synchronously ────────────
      // We read directly from store.getState() (not React hooks) so that
      // we don't need these values in the effect deps array.  They are all
      // resolved by the time sessionId is set in the queue store, and the
      // effect only needs to fire once per new sessionId.
      const queueState = useQueueStore.getState()
      const item =
        queueState.attentionItems.find((i) => i.session_id === sessionId) ??
        queueState.historyItems.find((i) => i.session_id === sessionId) ??
        null

      const snapshottedAgentId = queueState.agentSessionModalAgentId
      const snapshottedUserId  = queueState.agentSessionModalUserId

      const resolvedAgentId = snapshottedAgentId ?? item?.agent_id ?? null

      // Resolve user id: prefer the open-time snapshot, then the facets
      // the operations service attached to the attention item.
      const facets = (item?.details?.facets as Record<string, unknown> | undefined) ?? {}
      const facetUserId = typeof facets.user_id === 'string' ? facets.user_id : null
      const resolvedUserId = snapshottedUserId ?? facetUserId ?? null

      const consumerId = useOperatorStore.getState().selected?.consumer_id ?? ''
      const env        = useEnvironmentStore.getState().active

      // ── Close any existing window and open a fresh one ────────────────
      //
      // Previously we would focus an existing window for this session rather
      // than opening a new one. This caused a stale-transcript problem: the
      // existing window was showing the transcript as it appeared when it was
      // first opened, and the live-stream subscription inside it may not have
      // caught up with turns produced after the most recent operator action.
      // The operator had to close and reopen the window to see current data.
      //
      // Closing and reopening ensures the new window always fetches the latest
      // transcript from the server on mount. The overhead of a fresh WebviewWindow
      // is negligible compared to the confusion of seeing stale session content.
      // The close-then-reopen sequence, the 50 ms teardown pause and the
      // window construction all now live in `openPopout` (Task 4093), so this
      // component holds no Tauri dependency.
      const label = sessionLabel(sessionId)

      const params = new URLSearchParams()
      params.set('sessionId', sessionId)
      if (resolvedAgentId) params.set('agentId', resolvedAgentId)
      if (resolvedUserId)  params.set('userId', resolvedUserId)
      if (consumerId)      params.set('consumerId', consumerId)
      params.set('env', env)
      if (item?.title) params.set('title', item.title)

      // Load the same index.html with ?sv=1 so Tauri serves an existing file
      // from the bundle. The session-viewer mode is detected at module init in
      // main.tsx and renders SessionWindowApp instead of the full Stream app.
      // A separate HTML entry point was attempted but Vite 5 + Rollup silently
      // drops multi-entry HTML when a plugin only processes one root entry.
      const url = `index.html?sv=1&${params.toString()}`

      const windowTitle =
        item?.title?.trim() ||
        formatAgentName(resolvedAgentId)

      // Errors are reported rather than thrown: the operator can retry by
      // clicking the button again, and we fall through to
      // closeAgentSessionModal either way so the store is reset.
      const result = await openPopout({
        label,
        url,
        title:     windowTitle,
        width:     860,
        height:    1140,
        minWidth:  600,
        minHeight: 700,
      })
      if (!result.ok) {
        if (result.reason === 'failed') {
          console.error(
            '[AgentSessionModal] Failed to create session window:',
            result.error,
          )
          toast({
            title: 'Could not open the session',
            description: 'The window failed to open. Try again.',
            variant: 'danger',
          })
        } else {
          // 'unsupported' — no browser pop-out yet (Task 4094).
          //
          // This modal is NOT desktop-only, which the first pass at this fix
          // assumed. It is mounted unconditionally in AppShell, ships in
          // dist-web, and is opened from two in-scope surfaces — QueueView and
          // the Inbox inspector — so a portal operator clicking through to an
          // agent session hit exactly the dead click the other two modals were
          // fixed for: no window, no toast, no console line, store reset.
          toast({
            title: 'Session view is not available in the browser yet',
            description:
              'Pop-out windows are currently desktop-only. Open Stream on the Mac app to use this.',
          })
        }
      }

      // Reset the queue store immediately so the same session can be
      // re-opened without needing to first close it.
      queueState.closeAgentSessionModal()
    }

    void openSessionWindow()

    // Intentionally narrow dep array: we only want to fire when sessionId
    // transitions from null to a value.  All other inputs are read via
    // store.getState() snapshots inside openSessionWindow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  // The "modal" is an OS window — nothing to render in the main DOM.
  return null
}

export default AgentSessionModal
